import { Component, inject, OnInit } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { takeUntil } from 'rxjs';
import { Announcement, AnnouncementType } from '../../models/announcements';
import { AnnouncementDetailComponent } from '../announcement-details/announcement-details.component';
import { AnnouncementCreateComponent } from '../announcement-create/announcement-create.component';
import { PaginationComponent } from '../../../../shared/components/pagination/pagination.component';
import { PageSizeComponent } from '../../../../shared/components/page-size/page-size.component';
import { ActivatedRoute, Router } from '@angular/router';
import { SvgIconComponent } from '../../../../shared/components/svg-icon/svg-icon.component';
import { MenuComponentBase } from '../../../../shared/base-classes/menu-component-base';
import { AnnouncementsService } from '../../services/announcements.service';
import {
  TableComponent,
  TableColumn,
} from '../../../../shared/components/table/table.component';
import { SecondaryPopupComponent } from '../../../../shared/components/secondary-popup/secondary-popup.component';

@Component({
  selector: 'app-announcement',
  standalone: true,
  imports: [
    CommonModule,
    AnnouncementDetailComponent,
    AnnouncementCreateComponent,
    PaginationComponent,
    PageSizeComponent,
    SvgIconComponent,
    TableComponent,
    DatePipe,
    SecondaryPopupComponent,
  ],
  templateUrl: './announcement.component.html',
  styleUrl: './announcement.component.scss',
})
export class AnnouncementComponent extends MenuComponentBase implements OnInit {
  announcementsService = inject(AnnouncementsService);

  currentPage = 1;
  pageSize = 50;
  totalEmails = 0;
  loading = true;
  emails: Announcement[] = [];
  selectedEmail: Announcement | null = null;
  isComposing = false;

  router = inject(Router);
  override route = inject(ActivatedRoute);
  AnnouncementType = AnnouncementType;

  tableColumns: TableColumn[] = [
    { header: 'Recipients', width: '20%' },
    { header: 'Type', width: '16%' },
    { header: 'Subject', width: '22%' },
    { header: 'Message', width: '22%' },
    { header: 'Date', width: '12%' },
    { header: '', width: '8%' },
  ];

  override ngOnInit(): void {
    super.ngOnInit();
    this.currentPage = 1;
    this.loadEmails(this.currentPage, this.pageSize);
  }

  loadEmails(page: number, pageSize: number) {
    this.loading = true;
    this.announcementsService
      .getSentMails(page, pageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.emails = response.results;
          this.totalEmails = response.count;
          this.loading = false;
        },
        error: (error) => {
          console.error('Error fetching emails:', error);
          this.loading = false;
        },
      });
  }

  selectEmail(email: Announcement): void {
    this.selectedEmail = email;
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadEmails(this.currentPage, this.pageSize);
  }

  onPageSizeChange(newPageSize: number): void {
    if (newPageSize !== this.pageSize) {
      this.pageSize = newPageSize;
      this.currentPage = 1;
      this.loadEmails(this.currentPage, this.pageSize);
    }
  }

  onComposeClick(): void {
    this.isComposing = true;
  }

  onAnnouncementSent(): void {
    this.isComposing = false;
    this.loadEmails(this.currentPage, this.pageSize);
  }

  onAnnouncementCancelled(): void {
    this.isComposing = false;
  }

  getRecipientsEmails(email: Announcement): string {
    if (
      !email.recipients?.original_recipients?.connections?.length &&
      !email.recipients?.original_recipients?.users?.length
    ) {
      return '';
    }
    const recipients: string[] = [];
    email.recipients?.original_recipients?.connections?.forEach((conn) => {
      recipients.push(conn);
    });
    email.recipients?.original_recipients?.users?.forEach((user) => {
      recipients.push(user);
    });
    return recipients.join(', ');
  }

  onCloseDetailsPopup(): void {
    this.selectedEmail = null;
  }
}
